import { CrownTwoTone } from "@ant-design/icons";
import { Box, Flex, HStack, VStack } from "@chakra-ui/react";
import { Alert, Button, Form, Input, InputNumber, Select, Tag } from "antd";
import { Rule } from "antd/lib/form";
import { StaffRoutes } from "appRoutePaths";
import useAvailablePackageTypes from "hooks/useAvailablePackageTypes";
import { ApplicationUser, Owners } from "models/applicationUsers";
import { PackageTypeOption } from "models/common";
import { CorePackageTypeIds, PackageAmount, PackageType, StaffPackage } from "models/packages";
import { PackageQRCodeLink } from "pages/staff/NewPackage";
import { useState } from "react";
import { Link } from "react-router-dom";
import "styles/staff/new_package.css";
import { ownershipValidator } from "util/ownership";
import OwnershipSelector from "../OwnershipSelector";
import packageAmountOptions, { totalBatchCount } from "./packageAmountOptions";

const { Option } = Select;

export interface NewPackageFormData {
    amount?: PackageAmount;
    packageType: PackageType;
    owners: Owners;
    trackingNumberSuffix?: string;
    scanRowCount?: number;
    nonScanRowCount?: number;
}

const packageTypePriority: Record<string, number> = {
    [CorePackageTypeIds.Standard]: 0,
    [CorePackageTypeIds.Under20]: 1,
    [CorePackageTypeIds.Premium]: 2,
    [CorePackageTypeIds.Wax]: 3,
    '3ee5d920-6e7f-4a6a-b9f1-1db680db11a6': 4  // memorabilia
}

const packageTypeCustomSort = (a: PackageType, b: PackageType) => {
    const containsA = a.id in packageTypePriority;
    const containsB = b.id in packageTypePriority;

    if (!containsA && !containsB) {
        return 0;
    } else if (!containsA) {
        return 1;
    } else if (!containsB) {
        return -1;
    }

    return packageTypePriority[a.id] - packageTypePriority[b.id];
}

const ExtraCustomerDetail = ({ userName, notes, vip }: ApplicationUser) => {
    if (!notes && !vip) {
        return null;
    }

    const alertProps: any = vip
        ? {
            type: 'warning',
            icon: <CrownTwoTone twoToneColor='#faad14' />,
            message: <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>{userName}</div>
                <Tag color="orange" style={{ alignSelf: 'center' }}>VIP Customer</Tag>
            </div>
        }
        : {
            type: 'info',
            message: userName
        };

    return <Alert
        className="ant-alert-with-description"
        showIcon
        {...alertProps}
        description={notes}
        style={{ whiteSpace: 'pre', width: '100%' }}
    />;
}

const NewPackageForm = (props: {
    prevPackage?: StaffPackage;
    disabled: boolean;
    onSubmit: (form: NewPackageFormData) => void;
    packageType?: PackageType,
    customer?: ApplicationUser,
    amount?: PackageAmount,
    trackingNumberSuffix?: string
}) => {
    const [form] = Form.useForm<NewPackageFormData>();
    const [currentPkgType, setCurrentPkgType] = useState<
        PackageType | undefined
    >(props.packageType);
    const { data: types = [] } = useAvailablePackageTypes();
    const [defaultCustomer, setDefaultCustomer] = useState(props.customer);
    const [error, setError] = useState<string>();

    const { scanRowCount, nonScanRowCount, owners } = Form.useWatch([], form) ?? {};
    const customers = owners?.customers ?? [];

    const onPackageTypeChange = (_: string, option: PackageTypeOption | PackageTypeOption[]) => {
        const opt = option as PackageTypeOption;
        setCurrentPkgType(opt.packageType);
        setDefaultCustomer(undefined);

        form.resetFields(['owner']);
        form.setFieldsValue({
            owners: {
                customers: [],
                silentUsers: []
            },
            packageType: opt.packageType,
            amount: opt.packageType.usesAmounts
                ? undefined
                : PackageAmount.NotApplicable,
            scanRowCount: undefined,
            nonScanRowCount: undefined,
        });
    };

    const validateOwnership = async (_: Rule, owners: Owners) => {
        if (!currentPkgType) {
            throw new Error('Package type must be selected before package ownership can be validated');
        }
        await ownershipValidator(currentPkgType.supportSilentAccounts, owners);
    }

    const rowCountValidator = () => {
        if (scanRowCount === 0 && nonScanRowCount === 0) {
            return Promise.reject(new Error('Row counts cannot both be zero'));
        }

        return Promise.resolve();
    }

    const filteredPackageAmounts = currentPkgType?.usesAmounts
        ? packageAmountOptions.filter(o => o.value !== PackageAmount.NotApplicable)
        : packageAmountOptions.filter(o => o.value === PackageAmount.NotApplicable);

    const filteredAmountOptions = filteredPackageAmounts.map((o) => (
        <Option key={o.value} value={o.value}>
            {o.label}
        </Option>
    ));

    const splitCounts =
        currentPkgType?.createBatchesWithPackage &&
        currentPkgType?.usesAmounts &&
        !currentPkgType.combineRowCounts;

    return (
        <Form
            id="new-pkg"
            form={form}
            initialValues={{
                packageType: props.packageType,
                amount: props.amount,
                type: props.packageType?.id,
                owners: {
                    customers: defaultCustomer ? [defaultCustomer] : [],
                    silentUsers: []
                },
                trackingNumberSuffix: props.trackingNumberSuffix
            }}
            disabled={props.disabled}
            onFinish={props.onSubmit}
            layout="vertical"
        >
            <VStack align="start" minW="30rem" maxW="35rem" w="100%">
                <Flex w="100%" flexDirection={{ base: "column", lg: "row" }}>
                    <Box
                        flex={1}
                        as={Form.Item}
                        label="Package Type"
                        rules={[
                            {
                                required: true,
                                message: "Package type is required!",
                            },
                        ]}
                        name="type"
                    >
                        <Select
                            disabled={types.length === 0}
                            onChange={onPackageTypeChange}
                            options={types
                                .sort(packageTypeCustomSort)
                                .map(t =>
                                    ({ label: t.internalName, value: t.id, packageType: t }))
                            } />
                    </Box>
                    <Box hidden as={Form.Item} name="packageType">
                        <Input hidden />
                    </Box>
                    {!splitCounts && <Box
                        flex={1}
                        pl={6}
                        as={Form.Item}
                        label="Package Amount"
                        name="amount"
                        rules={[
                            {
                                required: !splitCounts,
                                message: "Package amount is required!",
                            },
                        ]}
                    >
                        <Select>{filteredAmountOptions}</Select>
                    </Box>}
                </Flex>
                <Box
                    as={Form.Item}
                    minWidth="30rem"
                    label="Tracking Number (Last 4)"
                    name="trackingNumberSuffix"
                    rules={[{ min: 4, max: 4, message: "Must be 4 characters" }]}
                >
                    <Input />
                </Box>
                {splitCounts && <Flex w="100%" flexDirection={{ base: "column", lg: "row" }}>
                    <Box
                        as={Form.Item}
                        flex={1}
                        pr={6}
                        label="Scannable Rows"
                        name="scanRowCount"
                        dependencies={['nonScanRowCount']}
                        rules={[
                            { required: currentPkgType?.usesAmounts, message: "Scannable count is required" },
                            { type: 'number', min: 0, message: "cannot be less zero" },
                            { validator: rowCountValidator }
                        ]}
                        extra={scanRowCount && scanRowCount > 0 ? `${totalBatchCount(scanRowCount, 10)} scannable batches` : undefined}>
                        <InputNumber style={{ width: '228px' }} step={0.25} min={0} />
                    </Box>

                    <Box
                        as={Form.Item}
                        flex={1}
                        label="Non-Scannable Rows"
                        name="nonScanRowCount"
                        dependencies={['scanRowCount']}
                        rules={[
                            { required: currentPkgType?.usesAmounts, message: "Non-scannable count is required" },
                            { type: 'number', min: 0, message: "cannot be less zero" },
                            { validator: rowCountValidator }
                        ]}
                        extra={nonScanRowCount && nonScanRowCount > 0 ? `${totalBatchCount(nonScanRowCount, 6)} non-scannable batches` : undefined}>
                        <InputNumber style={{ width: '228px' }} step={0.25} min={0} />
                    </Box>
                </Flex>}
                <Box
                    as={Form.Item}
                    minWidth="30rem"
                    help={error && <Alert showIcon type="error" message={error} />}
                    name="owners"
                    rules={[
                        { validator: validateOwnership }
                    ]}
                >
                    <OwnershipSelector
                        includeFrozenUsers={false}
                        supportSilentAccounts={currentPkgType?.supportSilentAccounts}
                        allowMultiple={currentPkgType?.allowMultipleOwners}
                        onError={setError}
                    />
                </Box>
                <HStack spacing={10} w='100%' display='flex' justifyContent='flex-end'>
                    {props.prevPackage && <div style={{ flex: 1 }}>
                        <PackageQRCodeLink id={props.prevPackage.id} text="Prev. QR Code" />
                    </div>}
                    <div>
                        <Button size="large" type="link">
                            <Link to={StaffRoutes.packages.url}>Cancel</Link>
                        </Button>
                        <Button size="large" type="primary" htmlType="submit">
                            Create
                        </Button>
                    </div>
                </HStack>

                {customers.map(c => (c && <ExtraCustomerDetail {...c} />))}
            </VStack>
        </Form>
    );
};

export default NewPackageForm;
